import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar, Alert
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { UserApiService } from "../services/api/UserAPIService";
import { formatDateTime } from "../shared/utils";
import { BUTTON, PLACEHOLDER } from "../shared/constants";
import axios from "axios";
import { ScheduleMessageApiService } from "../services/api/ScheduleMessageAPIService.js";

dayjs.extend(utc);
dayjs.extend(timezone);

const timezones = {
  PST: { label: "Pacific Time", value: "America/Los_Angeles" },
  MST: { label: "Mountain Time", value: "America/Denver" },
  CT:  { label: "Central Time",  value: "America/Chicago" },
  EST: { label: "Eastern Time",  value: "America/New_York" },
  GMT: { label: "Greenwich Mean Time", value: "Etc/GMT" },
};

const Conversation = ({ initialMessages, phone, totalMessages }) => {
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [minPage, setMinPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [noMoreOldMessages, setNoMoreOldMessages] = useState(false);
  const [noMoreNewMessages, setNoMoreNewMessages] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [sendOption, setSendOption] = useState("immediate");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedTime, setSelectedTime] = useState(dayjs());
  const [timezone, setTimezone] = useState("UTC");
  const [customMessage, setCustomMessage] = useState("");
  const conversationRef = useRef(null);
  const [snackData, setSnackData] = useState({
      show: false,
      message: "",
      type: "error",
    });

  // useEffect(() => {
  //   if (totalMessages) {
  //     const calculatedMaxPage = totalMessages?.pages;
  //     setMaxPage(calculatedMaxPage);
  //     setPage(calculatedMaxPage);
  //     setMinPage(1);
  //     fetchMessages(calculatedMaxPage, true);
  //   }
  // }, [totalMessages]);

  useEffect(() => {
    const loadAllMessagesInReverse = async () => {
      if (totalMessages) {
        const totalPages = totalMessages.pages;
        setMaxPage(totalPages);
        setMinPage(1); // Assuming minPage is always 1
        let allMessages = [];
        if(totalPages === 1)
        {
          allMessages = [...initialMessages];
        }
        else{
          for (let page = totalPages; page >= 1; page--) {
            const response = await fetchMessages(page, true, true);
            allMessages = [...response, ...allMessages]; // Prepend older
          }
        }

        setMessages(allMessages);
        setInitialLoaded(true);
      }
    };

    loadAllMessagesInReverse();
  }, [totalMessages]);

  useEffect(() => {
    if (initialLoaded && conversationRef.current) {
      const el = conversationRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [initialLoaded]);

  // useEffect(() => {
  //   const handleScroll = () => {
  //     if (!conversationRef.current || loading) return;
  //     const { scrollTop, scrollHeight, clientHeight } = conversationRef.current;

  //     if (scrollTop <= 10 && minPage > 1 && !noMoreOldMessages && initialLoaded) {
  //       fetchMessages(minPage - 1, true);
  //     }

  //     if (
  //       scrollTop + clientHeight >= scrollHeight - 10 &&
  //       page < maxPage &&
  //       !noMoreNewMessages
  //     ) {
  //       fetchMessages(page + 1, false);
  //     }
  //   };

  //   const conversationEl = conversationRef.current;
  //   if (conversationEl) {
  //     conversationEl.addEventListener("scroll", handleScroll);
  //   }

  //   return () => {
  //     if (conversationEl) {
  //       conversationEl.removeEventListener("scroll", handleScroll);
  //     }
  //   };
  // }, [loading, minPage, page, noMoreOldMessages, noMoreNewMessages]);

  const fetchMessages = async (requestedPage, isOlder, returnMessages = false) => {
    setLoading(true);
    try {
      let pagesize=999;
      const response = await UserApiService.userConversation(phone, requestedPage , pagesize);
      if (response.messages.length > 0) {
        if (!initialLoaded) setInitialLoaded(true);

        if (isOlder) {
          setMinPage(requestedPage);
          setMessages((prev) => [...response.messages, ...prev]);
        } else {
          setPage(requestedPage);
          setMessages((prev) => [...prev, ...response.messages]);
        }

        return returnMessages ? response.messages : null;
      } else {
        isOlder ? setNoMoreOldMessages(true) : setNoMoreNewMessages(true);
        return [];
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = (type) => {
    if(customMessage)
    {
      setOpenModal(type === "later" ? true : false);
      setSendOption(type);
    }
    else{
      setSnackData({
        show: true,
        message: "Please enter custom message you want to send",
        type: "error",
      });
    }
  };

  const handleSubmit = async () => {

    if (!customMessage || customMessage === "")
    {
      setSnackData({
                show: true,
                message: "Please enter custom message you want to send",
                type: "error",
              });

      return;
    }
    try {
      let payload = {
        From: phone,
        Body: customMessage || "", // You may want to get this from a TextField
        status: "new",
        category: "general-chat",
        source: "admin",
      };

      if (sendOption === "later") {

        const scheduledDateTime = dayjs.tz(
          `${selectedDate.format("YYYY-MM-DD")} ${selectedTime.format("HH:mm")}`,
          timezones[timezone].value
        );

        payload.Schedule = scheduledDateTime.utc().format("YYYY-MM-DDTHH:mm:ssZ");
        payload.timezone = timezones[timezone].value; // optional
      }
      const code = process.env.REACT_APP_API_CODE;

      ScheduleMessageApiService.scheduleMessage(payload, code)
        .then((response) => {
          if (response.status === 200) {
            console.log("Message successfully sent/scheduled:", response.data);
          } else {
            console.error("Failed to send message:", response.data);
          }
        })
        .catch((err) => console.error("Error fetching messages:", err));
    } catch (error) {
      console.error("Error sending message:", error);
    }
    setCustomMessage("");

    setOpenModal(false);
  };

  return (
    <>
      <Box
        ref={conversationRef}
        mt={2}
        p={2}
        sx={{
          borderTop: "2px solid #ccc",
          overflowY: "auto",
          maxHeight: "500px",
        }}
      >
        {loading && (
          <CircularProgress sx={{ display: "block", margin: "auto" }} />
        )}
        {messages.map((msg, index) => (
          <Box
            key={index}
            sx={{ mb: 2, textAlign: msg.source === "user" ? "left" : "right" }}
          >
            {/* Media Images */}
                        {msg.media_urls?.length > 0 ? (
                          <Box
                            sx={{
                              display: "inline-block",
                              p: 1,
                              borderRadius: "8px",
                              bgcolor: msg.source === "user" ? "#e1e1e1" : "#eee",
                              color: msg.source === "user" ? "white" : "black",
                              maxWidth: "80%",
                              textAlign: "left",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                justifyContent:
                                  msg.source === "user" ? "flex-start" : "flex-end",
                                mb: 1,
                              }}
                            >
                              {msg.media_urls.map((url, idx) => (
                                <Box
                                  key={idx}
                                  component="img"
                                  src={url}
                                  alt={`media-${idx}`}
                                  sx={{
                                    width: "200px",
                                    height: "auto",
                                    borderRadius: 2,
                                    objectFit: "fill",
                                    border: "1px solid #ccc",
                                  }}
                                />
                              ))}
                            </Box>

                            <Typography
                              sx={{
                                display: "inline-block",
                                p: 1,
                                borderRadius: "8px",
                                bgcolor: msg.source === "user" ? "#005CFF" : "#eee",
                                color: msg.source === "user" ? "white" : "black",
                                maxWidth: "100%",
                                textAlign: "left",
                              }}
                            >
                              {msg.body}
                            </Typography>
                          </Box>
                        )
                      :
                      <Typography
                              sx={{
                                display: "inline-block",
                                p: 1,
                                borderRadius: "8px",
                                bgcolor: msg.source === "user" ? "#005CFF" : "#eee",
                                color: msg.source === "user" ? "white" : "black",
                                maxWidth: "80%",
                                textAlign: "left",
                              }}
                            >
                              {msg.body}
                            </Typography>
                      }
            <Typography
              variant="caption"
              display="block"
              sx={{ fontStyle: "italic", color: "gray" }}
            >
              {msg?.source?.charAt(0)?.toUpperCase() + msg?.source?.slice(1)}{" "}
              sent on {formatDateTime(msg?.received_at)}
            </Typography>
          </Box>
        ))}
        {noMoreOldMessages && (
          <Typography
            variant="body2"
            color="textSecondary"
            textAlign="center"
            mt={2}
          >
            No older messages available.
          </Typography>
        )}
      </Box>

      {/* Send Message Button */}
      <Box
        mt={2}
        sx={{ display: "flex", alignItems: "center", padding: "8px" }}
      >
        <TextField
          fullWidth
          placeholder={PLACEHOLDER.CUSTOME_MESSAGE}
          variant="outlined"
          size="small"
          value={customMessage}
          onChange={(e) => setCustomMessage(e.target.value)}
        />
        <Box>
          <Button
            variant="contained"
            sx={{
              bgcolor: "#005CFF",
              color: "white",
              ml: 1,
              textTransform: "none",
              marginTop: "10px",
              width: "100%",
            }}
            onClick={(e) => handleSubmit()}
          >
            Send Now
          </Button>
          <Button
            variant="contained"
            sx={{
              bgcolor: "#005CFF",
              color: "white",
              ml: 1,
              textTransform: "none",
              marginTop: "10px",
              width: "100%",
            }}
            onClick={(e) => handleSendMessage("later")}
          >
            Schedule Send
          </Button>
        </Box>
      </Box>

      {/* Modal for Scheduling Message */}
      {/* <Modal open={openModal} onClose={() => setOpenModal(false)}> */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Schedule Send For Later</DialogTitle>
        <DialogContent>
          {sendOption === "later" && (
            <>
              <Box sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{ width: "30%" }}
                    label="Select Date"
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    disablePast
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                  <TimePicker
                    sx={{ marginLeft: "10px", width: "30%" }}
                    label="Select Time"
                    value={selectedTime}
                    onChange={(newValue) => setSelectedTime(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth sx={{ mt: 2, ml: 2 }} />
                    )}
                  />
                </LocalizationProvider>

                <FormControl fullWidth sx={{ ml: 2, width: "30%" }}>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                  >
                    {Object.entries(timezones).map(([key, tz]) => (
                      <MenuItem key={key} value={key}>
                        {tz.label}
                      </MenuItem>
                    ))}

                  </Select>
                </FormControl>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Schedule Message
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
              style={{ top: "80px" }}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              open={snackData.show}
              autoHideDuration={3000}
              onClose={() => setSnackData({ show: false })}
            >
              <Alert
                onClose={() => setSnackData({ show: false })}
                severity={snackData.type}
              >
                {snackData.message}
              </Alert>
            </Snackbar>

    </>
  );
};

export default Conversation;
